import type { MatchRepository } from "@/modules/leagues/repositories/MatchRepository";
import type { Match, ParticipantPrediction } from "@/modules/leagues/types/match";
import { isApiFootballCompetition } from "@/config/apiFootballCompetitions";

/**
 * Camada única pedida no documento da segunda API: "o restante do
 * aplicativo NÃO deve precisar saber qual API forneceu o dado". Todo
 * consumidor (hooks, telas, o cron de pontuação) continua chamando
 * `matchRepository.getByRound(...)` normalmente — este roteador decide,
 * por `competitionId`, se a liga usa football-data.org (API atual,
 * intocada) ou o cache da API-Football, e delega inteiramente.
 */
export class CompositeMatchRepository implements MatchRepository {
  constructor(
    private readonly primary: MatchRepository, // football-data.org — API atual
    private readonly apiFootball: MatchRepository, // cache da API-Football
    private readonly getCompetitionId: (leagueId: string) => Promise<string | null>
  ) {}

  private async resolve(leagueId: string): Promise<MatchRepository> {
    const competitionId = await this.getCompetitionId(leagueId);
    if (competitionId && isApiFootballCompetition(competitionId)) return this.apiFootball;
    return this.primary;
  }

  async getByRound(leagueId: string, round?: number): Promise<Match[]> {
    return (await this.resolve(leagueId)).getByRound(leagueId, round);
  }

  async getById(leagueId: string, matchId: string): Promise<Match | null> {
    return (await this.resolve(leagueId)).getById(leagueId, matchId);
  }

  async savePrediction(leagueId: string, matchId: string, homeScore: number, awayScore: number): Promise<void> {
    return (await this.resolve(leagueId)).savePrediction(leagueId, matchId, homeScore, awayScore);
  }

  async getParticipantPredictions(leagueId: string, matchId: string): Promise<ParticipantPrediction[]> {
    return (await this.resolve(leagueId)).getParticipantPredictions(leagueId, matchId);
  }

  async getAvailableRounds(leagueId: string): Promise<Array<{ round: number; label: string }>> {
    return (await this.resolve(leagueId)).getAvailableRounds(leagueId);
  }
}
